import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Users, Wand2, Trash2, Pencil, X, Camera, Upload, ImageIcon, Loader2, Play, LayoutGrid } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { fetchMurfVoiceCatalog } from '@/lib/murfApi';
import { NarrationVoicePickerDialog } from '@/components/NarrationVoicePickerDialog';
import { useImageStyleConversion } from '../hooks/useImageStyleConversion';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const roles = ['hero', 'friend', 'villain', 'animal', 'imaginary', 'narrator'];
const genderOptions = [
  { value: 'not_specified', label: 'Not specified' },
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'other', label: 'Other' },
];
const traitOptions = ['brave', 'shy', 'funny', 'curious', 'kind', 'adventurous', 'smart', 'gentle', 'playful', 'creative'];
const speakingStyles = ['funny', 'shy', 'brave', 'curious', 'cheerful', 'wise'];

const defaultPersonalityProfile = {
  aggression: 50,
  humor: 50,
  loyalty: 50,
  intelligence: 50,
  emotional_depth: 50,
  chaos: 50,
};

const personalitySliderFields = [
  { key: 'aggression', label: 'Aggression' },
  { key: 'humor', label: 'Humor' },
  { key: 'loyalty', label: 'Loyalty' },
  { key: 'intelligence', label: 'Intelligence' },
  { key: 'emotional_depth', label: 'Emotional depth' },
  { key: 'chaos', label: 'Chaos' },
];

/** Labels map to Edge TTS presets (male = deep / “heroic”, etc.). */
const voiceStyleOptions = [
  { value: '__none__', label: 'Not set' },
  { value: 'male', label: 'Heroic' },
  { value: 'storyteller', label: 'Storyteller' },
  { value: 'female', label: 'Warm' },
  { value: 'child', label: 'Youthful' },
];

const emptyChar = {
  name: '',
  role: 'hero',
  gender: 'not_specified',
  species: 'human',
  appearance: '',
  description: '',
  personality_traits: [],
  speaking_style: '',
  personality_profile: { ...defaultPersonalityProfile },
  voice_style: 'male',
  elevenlabs_voice_id: '',
  murf_voice_id: '',
  murf_locale: '',
  minimax_voice_id: '',
  dialogue_tts_provider: 'runway',
  runway_model: 'eleven_multilingual_v2',
  runway_voice_id: 'Leslie',
  is_imaginary: false,
};
const roleColors = {
  hero: '#6366F1',
  friend: '#F59E0B',
  villain: '#EF4444',
  animal: '#10B981',
  imaginary: '#EC4899',
  narrator: '#8B5CF6',
};
const voiceProviderLabel = { elevenlabs: 'ElevenLabs', murf: 'Murf', minimax: 'MiniMax', runway: 'Runway ML' };
const runwayAudioModels = [
  { value: 'eleven_multilingual_v2', label: 'eleven_multilingual_v2' },
  { value: 'eleven_text_to_sound_v2', label: 'eleven_text_to_sound_v2' },
  { value: 'eleven_voice_isolation', label: 'eleven_voice_isolation' },
  { value: 'eleven_voice_dubbing', label: 'eleven_voice_dubbing' },
  { value: 'eleven_multilingual_sts_v2', label: 'eleven_multilingual_sts_v2' },
];

function pickBySeed(values, seed) {
  if (!Array.isArray(values) || values.length === 0) return 'Leslie';
  const s = String(seed || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return values[h % values.length] || values[0] || 'Leslie';
}

function inferRunwayVoiceIdForCharacter(charLike) {
  const traits = Array.isArray(charLike?.personality_traits)
    ? charLike.personality_traits.map((t) => String(t || '').toLowerCase())
    : [];
  const role = String(charLike?.role || '').toLowerCase();
  const style = String(charLike?.voice_style || '').toLowerCase();
  const gender = String(charLike?.gender || '').toLowerCase();
  const profile = charLike?.personality_profile || {};
  const aggression = Number(profile.aggression || 50);
  const humor = Number(profile.humor || 50);
  const chaos = Number(profile.chaos || 50);
  const emotionalDepth = Number(profile.emotional_depth || 50);
  const seed = `${charLike?.id || ''}:${charLike?.name || ''}:${role}`;

  if (style === 'child') return pickBySeed(['Katie', 'Noah', 'Ella', 'James'], seed);
  if (role === 'villain' || chaos >= 70 || aggression >= 75) {
    return pickBySeed(['Ragnar', 'Vincent', 'Kirk', 'Clint'], seed);
  }
  if (traits.includes('funny') || traits.includes('playful') || humor >= 65) {
    return pickBySeed(['Jack', 'Billy', 'Mabel', 'Pip'], seed);
  }
  if (traits.includes('shy') || traits.includes('gentle') || traits.includes('kind') || emotionalDepth >= 70) {
    return pickBySeed(['Serene', 'Maya', 'Rachel', 'Miriam'], seed);
  }
  if (traits.includes('brave') || traits.includes('adventurous') || role === 'hero') {
    return pickBySeed(['Mark', 'Clint', 'Arjun', 'Benjamin'], seed);
  }
  if (gender === 'female') return pickBySeed(['Maya', 'Serene', 'Leslie', 'Rachel'], seed);
  if (gender === 'male') return pickBySeed(['Mark', 'Arjun', 'Clint', 'James'], seed);
  return pickBySeed(['Leslie', 'Maya', 'Mark', 'Arjun'], seed);
}

const RUNWAY_LIVE_AVATAR_PRESETS = [
  { value: 'victoria', label: 'Victoria' },
  { value: 'vincent', label: 'Vincent' },
  { value: 'clara', label: 'Clara' },
  { value: 'drew', label: 'Drew' },
  { value: 'skye', label: 'Skye' },
  { value: 'max', label: 'Max' },
  { value: 'morgan', label: 'Morgan' },
  { value: 'felix', label: 'Felix' },
  { value: 'mia', label: 'Mia' },
  { value: 'marcus', label: 'Marcus' },
  { value: 'summer', label: 'Summer' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'aurora', label: 'Aurora' },
  { value: 'jasper', label: 'Jasper' },
  { value: 'leo', label: 'Leo' },
  { value: 'adrian', label: 'Adrian' },
  { value: 'nina', label: 'Nina' },
  { value: 'emma', label: 'Emma' },
  { value: 'blake', label: 'Blake' },
  { value: 'david', label: 'David' },
  { value: 'maya', label: 'Maya' },
  { value: 'nathan', label: 'Nathan' },
  { value: 'sam', label: 'Sam' },
  { value: 'georgia', label: 'Georgia' },
  { value: 'petra', label: 'Petra' },
  { value: 'adam', label: 'Adam' },
  { value: 'zach', label: 'Zach' },
  { value: 'violet', label: 'Violet' },
  { value: 'roman', label: 'Roman' },
  { value: 'luna', label: 'Luna' },
];

const CHARACTER_REFERENCE_GRID = [
  { key: 'faceFront', label: 'Face • F' },
  { key: 'faceBack', label: 'Face • B' },
  { key: 'faceLeft', label: 'Face • L' },
  { key: 'faceRight', label: 'Face • R' },
  { key: 'bodyFront', label: 'Body • F' },
  { key: 'bodyBack', label: 'Body • B' },
  { key: 'bodyLeft', label: 'Body • L' },
  { key: 'bodyRight', label: 'Body • R' },
];

function gridSrcForAssetPath(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  if (raw.startsWith('/api/')) return `${BACKEND_URL}${raw}`;
  if (raw.startsWith('http') || raw.startsWith('data:')) return raw;
  return null;
}

export const CharacterBuilderPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const storyFlowNewCharRef = useRef(false);
  const storyFlowEditCharRef = useRef(null);
  const [characters, setCharacters] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyChar });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [uploadedCharacterUrl, setUploadedCharacterUrl] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState('cartoon');
  const [showStyleOptions, setShowStyleOptions] = useState(false);
  const [useStyledVersion, setUseStyledVersion] = useState(false);
  const fileInputRef = useRef(null);
  const [elevenLabsVoices, setElevenLabsVoices] = useState([]);
  const [murfVoices, setMurfVoices] = useState([]);
  const [minimaxVoices, setMinimaxVoices] = useState([]);
  const [runwayVoices, setRunwayVoices] = useState([]);
  const [ttsCatalogLoading, setTtsCatalogLoading] = useState(false);
  const [voicePickerOpen, setVoicePickerOpen] = useState(false);
  const [voicePickerProvider, setVoicePickerProvider] = useState('elevenlabs');
  const [previewingVoiceId, setPreviewingVoiceId] = useState('');
  const [previewingVoiceStyle, setPreviewingVoiceStyle] = useState(false);
  const [refSheetDialogOpen, setRefSheetDialogOpen] = useState(false);
  const [refSheetDialogText, setRefSheetDialogText] = useState('');
  const [refSheetGeneratingByChar, setRefSheetGeneratingByChar] = useState({});
  const [refSheetSnapshots, setRefSheetSnapshots] = useState({});
  const [runwayLiveAvatarVoice, setRunwayLiveAvatarVoice] = useState('mia');
  const [runwayLiveAvatarBusy, setRunwayLiveAvatarBusy] = useState(false);

  // Image style conversion hook
  const {
    templates,
    loadingTemplates,
    converting,
    styledResult,
    fetchTemplates,
    convertImageStyle,
    resetConversion
  } = useImageStyleConversion();

  useEffect(() => { loadCharacters(); }, []);

  const refreshReferenceSheetSnapshots = useCallback(async (charId) => {
    if (!charId) return;
    try {
      const { data } = await api.get(`/characters/${charId}`, { timeout: 60000 });
      const rf = data?.creative_profile?.character?.referenceFrames;
      setRefSheetSnapshots((prev) => ({ ...prev, [charId]: rf && typeof rf === 'object' ? rf : {} }));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!editId) {
      setRefSheetSnapshots({});
      return;
    }
    refreshReferenceSheetSnapshots(editId);
  }, [editId, refreshReferenceSheetSnapshots]);

  const loadTtsVoiceCatalogs = useCallback(async () => {
    setTtsCatalogLoading(true);
    try {
      const [elRes, murfRes, minimaxRes, runwayRes] = await Promise.all([
        api.get('/tts/voices/elevenlabs'),
        fetchMurfVoiceCatalog(api),
        api.get('/tts/voices/minimax'),
        api.get('/tts/voices/runway'),
      ]);
      setElevenLabsVoices(elRes.data?.voices || []);
      setMurfVoices(murfRes.data?.voices || []);
      setMinimaxVoices(minimaxRes.data?.voices || []);
      setRunwayVoices(runwayRes.data?.voices || []);
    } catch (err) {
      toast.error('Could not load voice libraries');
    } finally {
      setTtsCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showForm) {
      loadTtsVoiceCatalogs();
    }
  }, [showForm, loadTtsVoiceCatalogs]);

  const voiceListForPicker = useMemo(() => {
    if (voicePickerProvider === 'elevenlabs') return elevenLabsVoices;
    if (voicePickerProvider === 'murf') return murfVoices;
    if (voicePickerProvider === 'runway') return runwayVoices;
    return minimaxVoices;
  }, [voicePickerProvider, elevenLabsVoices, murfVoices, minimaxVoices, runwayVoices]);

  const currentEditedCharacter = useMemo(
    () => (editId ? (characters.find((c) => c.id === editId) || null) : null),
    [characters, editId]
  );

  const runwayFaceFrontReady = useMemo(() => {
    if (!editId) return false;
    const v = refSheetSnapshots[editId]?.faceFront;
    return typeof v === 'string' && v.includes('/api/media/');
  }, [editId, refSheetSnapshots]);

  const creativeProfilePortraitSrc = useMemo(() => {
    const localPreview = typeof photoPreview === 'string' && photoPreview.trim() ? photoPreview.trim() : '';
    if (localPreview) return localPreview;
    const uploaded = typeof uploadedCharacterUrl === 'string' && uploadedCharacterUrl.trim() ? uploadedCharacterUrl.trim() : '';
    if (uploaded) return uploaded.startsWith('/api') ? `${BACKEND_URL}${uploaded}` : uploaded;
    const refImg = typeof currentEditedCharacter?.reference_image === 'string'
      ? currentEditedCharacter.reference_image.trim()
      : '';
    if (refImg) return refImg.startsWith('/api') ? `${BACKEND_URL}${refImg}` : refImg;
    const styled = typeof currentEditedCharacter?.styled_photo_url === 'string'
      ? currentEditedCharacter.styled_photo_url.trim()
      : '';
    if (styled) return styled.startsWith('/api') ? `${BACKEND_URL}${styled}` : styled;
    return '';
  }, [photoPreview, uploadedCharacterUrl, currentEditedCharacter]);

  const openVoicePicker = (provider) => {
    setVoicePickerProvider(provider);
    setVoicePickerOpen(true);
    loadTtsVoiceCatalogs();
  };

  const selectCharacterVoice = (voice) => {
    if (voicePickerProvider === 'elevenlabs') setForm((prev) => ({ ...prev, elevenlabs_voice_id: voice.voice_id }));
    if (voicePickerProvider === 'murf') {
      setForm((prev) => ({
        ...prev,
        murf_voice_id: voice.voice_id,
        murf_locale: voice.locale || voice.effective_locale || prev.murf_locale,
      }));
    }
    if (voicePickerProvider === 'minimax') setForm((prev) => ({ ...prev, minimax_voice_id: voice.voice_id }));
    if (voicePickerProvider === 'runway') setForm((prev) => ({ ...prev, runway_voice_id: voice.voice_id }));
    setVoicePickerOpen(false);
  };

  const playVoiceStylePreview = async () => {
    const vs = (form.voice_style || '').trim();
    if (!vs) {
      toast.error('Choose a voice style first');
      return;
    }
    setPreviewingVoiceStyle(true);
    try {
      const { data } = await api.post(
        '/tts/voice-style/sample',
        {
          voice_style: vs,
          text: 'This is how I sound when I tell the story—listen for tone and energy.',
        },
        { responseType: 'blob' },
      );
      const sampleUrl = URL.createObjectURL(data);
      const audio = new Audio(sampleUrl);
      audio.onended = () => URL.revokeObjectURL(sampleUrl);
      await audio.play();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not play voice style preview');
    } finally {
      setPreviewingVoiceStyle(false);
    }
  };

  const playVoiceSample = async (voice) => {
    if (!voice?.voice_id) return;
    if (voice.preview_url) {
      new Audio(voice.preview_url).play();
      return;
    }
    setPreviewingVoiceId(voice.voice_id);
    try {
      const { data } = await api.post(
        '/tts/voices/sample',
        { provider: voicePickerProvider, voice_id: voice.voice_id, locale: voice.locale },
        { responseType: 'blob' }
      );
      const sampleUrl = URL.createObjectURL(data);
      const audio = new Audio(sampleUrl);
      audio.onended = () => URL.revokeObjectURL(sampleUrl);
      await audio.play();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not play voice sample');
    } finally {
      setPreviewingVoiceId('');
    }
  };

  const loadCharacters = async () => {
    try {
      const { data } = await api.get('/characters');
      setCharacters(data);
    } catch (err) { toast.error('Failed to load characters'); }
    finally { setLoading(false); }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPEG, PNG, or WEBP images allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleConvertStyle = async () => {
    if (!uploadedCharacterUrl) {
      toast.error('Please upload a character photo first');
      return;
    }
    try {
      await convertImageStyle(uploadedCharacterUrl, selectedStyle);
      setUseStyledVersion(true);
    } catch {
      // convertImageStyle already reports the error.
    }
  };

  const uploadPhoto = async (charId) => {
    if (!photoFile) return;
    setUploadingPhoto(charId);
    try {
      const formData = new FormData();
      formData.append('file', photoFile);
      const { data } = await api.post(`/characters/${charId}/upload-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCharacters(prev => prev.map(c => c.id === charId ? data : c));
      setUploadedCharacterUrl(data.reference_image);
      toast.success('Photo uploaded!');
      setPhotoFile(null);
      setPhotoPreview(null);
      
      // Show cartoonization options after upload
      setShowStyleOptions(true);
      await fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally { setUploadingPhoto(null); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      let charData;
      if (editId) {
        const { data } = await api.put(`/characters/${editId}`, form);
        charData = data;
        setCharacters(characters.map(c => c.id === editId ? data : c));
        toast.success('Character updated!');
      } else {
        const { data } = await api.post('/characters', form);
        charData = data;
        setCharacters([...characters, data]);
        toast.success('Character created!');
      }
      
      // Upload photo if provided
      if (photoFile && charData?.id) {
        await uploadPhoto(charData.id);
      }
      
      // Save styled image if user selected it
      if (styledResult && charData?.id) {
        try {
          const { data: updatedChar } = await api.post(`/characters/${charData.id}/save-styled-image`, {
            styled_image_b64: styledResult.base64,
            style: selectedStyle,
            use_styled: useStyledVersion
          });
          setCharacters(prev => prev.map(c => c.id === charData.id ? updatedChar : c));
          toast.success(useStyledVersion ? `${selectedStyle} style saved!` : 'Original version selected');
        } catch (err) {
          console.error('Failed to save styled image:', err);
          toast.error('Character saved, but styled image failed to save');
        }
      }

      if (charData?.id) {
        await refreshReferenceSheetSnapshots(charData.id);
      }

      const returnTo = location.state?.returnTo;
      if (returnTo && charData?.id) {
        navigate(returnTo, {
          state: {
            selectedCharacters: [charData.id],
            restoredFromCharacterBuilder: true,
          },
        });
        resetForm();
        return;
      }

      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleUploadForExisting = async (charId) => {
    fileInputRef.current?.click();
    fileInputRef.current.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
      if (!allowed.includes(file.type)) { toast.error('Only JPEG, PNG, WEBP'); return; }
      if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
      setUploadingPhoto(charId);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await api.post(`/characters/${charId}/upload-photo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setCharacters(prev => prev.map(c => c.id === charId ? data : c));
        toast.success('Photo uploaded!');
        
        // Optionally trigger cartoonization flow for existing characters
        // (For now, we'll keep it simple - cartoonization only in the form)
      } catch (err) { toast.error('Upload failed'); }
      finally { setUploadingPhoto(null); fileInputRef.current.value = ''; }
    };
  };

  const handleCartoonize = async () => {
    if (!uploadedCharacterUrl) {
      toast.error('Please upload a photo first');
      return;
    }
    try {
      await startCartoonization(uploadedCharacterUrl, selectedTemplate);
    } catch (error) {
      // Error already toasted in hook
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/characters/${id}`);
      setCharacters(characters.filter(c => c.id !== id));
      toast.success('Character deleted');
    } catch (err) { toast.error('Failed to delete'); }
  };

  const openReferenceSheetPreview = async () => {
    if (!editId) return;
    try {
      const { data } = await api.post(`/characters/${editId}/reference-sheet/preview`);
      const sheet = data?.characterReferenceSheet;
      setRefSheetDialogText(sheet ? JSON.stringify(sheet, null, 2) : '{}');
      setRefSheetDialogOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    }
  };

  const runReferenceSheetGeneration = async () => {
    if (!editId) return;
    const targetCharId = editId;
    setRefSheetGeneratingByChar((prev) => ({ ...prev, [targetCharId]: true }));
    try {
      const { data } = await api.post(
        `/characters/${targetCharId}/reference-sheet/generate-runway`,
        { parallel: false },
        { timeout: 920000 },
      );
      const errN = Array.isArray(data?.errors) ? data.errors.length : 0;
      if (errN > 0) toast.warning(`${8 - errN}/8 Runway angles saved.`);
      else toast.success('Saved 8 turnaround views (Runway) on creative_profile.');
      await loadCharacters();
      await refreshReferenceSheetSnapshots(targetCharId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Runway generation failed');
    } finally {
      setRefSheetGeneratingByChar((prev) => ({ ...prev, [targetCharId]: false }));
    }
  };

  const createRunwayLiveAvatarFromBuilder = async () => {
    if (!editId || !runwayFaceFrontReady) return;
    setRunwayLiveAvatarBusy(true);
    try {
      const { data } = await api.post(
        `/characters/${editId}/runway-live-avatar`,
        { voice_preset_id: runwayLiveAvatarVoice },
        { timeout: 130000 },
      );
      const av = data?.runway_live_avatar;
      const st = av?.status != null ? String(av.status) : '';
      const aid = av?.id != null ? String(av.id) : '';
      toast.success(
        aid ? `Runway Live avatar created (${aid.slice(0, 8)}…).${st ? ` ${st}` : ''}` : 'Runway Live avatar requested.',
      );
      await loadCharacters();
      await refreshReferenceSheetSnapshots(editId);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Runway Live avatar failed');
    } finally {
      setRunwayLiveAvatarBusy(false);
    }
  };

  const startEdit = (char) => {
    const mergedProfile = { ...defaultPersonalityProfile };
    if (char.personality_profile && typeof char.personality_profile === 'object') {
      personalitySliderFields.forEach(({ key }) => {
        const raw = char.personality_profile[key];
        if (raw === undefined || raw === null || raw === '') return;
        const n = Number(raw);
        if (!Number.isFinite(n)) return;
        mergedProfile[key] = Math.max(0, Math.min(100, Math.round(n)));
      });
    }
    setForm({
      name: char.name,
      role: char.role,
      gender: char.gender || 'not_specified',
      species: char.species || (char.role === 'animal' ? 'animal' : 'human'),
      appearance: char.appearance || '',
      description: char.description,
      personality_traits: char.personality_traits || [],
      speaking_style: char.speaking_style || '',
      personality_profile: mergedProfile,
      voice_style: char.voice_style || '',
      elevenlabs_voice_id: char.elevenlabs_voice_id || '',
      murf_voice_id: char.murf_voice_id || '',
      murf_locale: char.murf_locale || '',
      minimax_voice_id: char.minimax_voice_id || '',
      dialogue_tts_provider: char.dialogue_tts_provider || 'runway',
      runway_model: char.runway_model || 'eleven_multilingual_v2',
      runway_voice_id: char.runway_voice_id || inferRunwayVoiceIdForCharacter(char),
      is_imaginary: char.is_imaginary || false,
    });
    const pv = char?.runway_live_avatar?.voice?.presetId || char?.runway_live_avatar?.voice?.preset_id;
    setRunwayLiveAvatarVoice(typeof pv === 'string' && pv.trim() ? pv.trim().toLowerCase() : 'mia');
    setEditId(char.id);
    setPhotoFile(null);
    setPhotoPreview(char.reference_image || null);
    setShowForm(true);
  };

  const resetForm = () => { 
    setForm({ ...emptyChar }); 
    setEditId(null); 
    setShowForm(false); 
    setPhotoFile(null); 
    setPhotoPreview(null); 
    setUploadedCharacterUrl(null);
    setRunwayLiveAvatarVoice('mia');
    setShowStyleOptions(false);
    setUseStyledVersion(false);
    resetConversion();
  };

  useEffect(() => {
    if (storyFlowNewCharRef.current) return;
    const st = location.state;
    const openFromQuery = new URLSearchParams(location.search).get('new') === '1';
    if (!st?.openNewCharacterForm && !openFromQuery) return;
    storyFlowNewCharRef.current = true;
    resetForm();
    setShowForm(true);
    const returnTo = st?.returnTo;
    navigate(location.pathname, { replace: true, state: returnTo ? { returnTo } : {} });
  }, [location.search, location.state, location.pathname, navigate]);

  useEffect(() => {
    const editFromQuery = new URLSearchParams(location.search).get('edit');
    const id = location.state?.editCharacterId || editFromQuery;
    const returnTo = location.state?.returnTo;
    if (!id || !characters.length) return;
    const key = `${id}:${returnTo || ''}`;
    if (storyFlowEditCharRef.current === key) return;
    const c = characters.find((x) => x.id === id);
    if (!c) return;
    storyFlowEditCharRef.current = key;
    startEdit(c);
    navigate(location.pathname, { replace: true, state: returnTo ? { returnTo } : {} });
  }, [characters, location.search, location.state, location.pathname, navigate]);

  const toggleTrait = (trait) => {
    setForm(prev => ({
      ...prev,
      personality_traits: prev.personality_traits.includes(trait)
        ? prev.personality_traits.filter(t => t !== trait)
        : [...prev.personality_traits, trait]
    }));
  };

  useEffect(() => {
    if (!showForm) return;
    if ((form.dialogue_tts_provider || 'runway') !== 'runway') return;
    if ((form.runway_voice_id || '').trim()) return;
    const inferred = inferRunwayVoiceIdForCharacter(form);
    if (!inferred) return;
    setForm((prev) => ({ ...prev, runway_voice_id: inferred }));
  }, [
    showForm,
    form.dialogue_tts_provider,
    form.runway_voice_id,
    form.name,
    form.role,
    form.gender,
    form.voice_style,
    form.personality_traits,
    form.personality_profile,
  ]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="character-builder-page">
      {/* Hidden file input for existing characters */}
      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
          <div>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight">
              <span className="gradient-text">Character</span> Builder
            </h1>
            <p className="text-[#64748B] mt-1">Create the heroes and friends for your stories</p>
          </div>
          <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-character-btn"
            className="rounded-full bg-[#6366F1] hover:bg-[#4F46E5] text-white font-accent px-6 py-5 shadow-lg shadow-[#6366F1]/25 hover:scale-105 active:scale-95 transition-all">
            <Plus className="w-5 h-5 mr-2" /> Add Character
          </Button>
        </div>

        {/* Character Form Modal */}
        <AnimatePresence>
          {showForm && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20 backdrop-blur-sm">
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-3xl p-8 w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl" data-testid="character-form-modal">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading font-bold text-xl">{editId ? 'Edit Character' : 'New Character'}</h2>
                  <button onClick={resetForm} className="text-[#94A3B8] hover:text-[#64748B]" data-testid="close-character-form"><X className="w-5 h-5" /></button>
                </div>

                <form onSubmit={handleSave} className="space-y-5">
                  {location.state?.returnTo === '/stories/new' && (
                    <p className="text-xs text-[#64748B] rounded-xl bg-slate-50 border border-slate-100 px-3 py-2 leading-relaxed">
                      <span className="font-semibold text-[#1E293B]">Name</span> is required. Reference photo, traits, and voice fields are optional—you can add them later in this builder.
                    </p>
                  )}

                  {/* Photo Upload */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Camera className="w-4 h-4 text-[#6366F1]" /> Reference photo <span className="text-xs font-normal text-[#94A3B8]">(optional)</span>
                    </Label>
                    <div className="flex items-center gap-4">
                      <div
                        onClick={() => document.getElementById('char-photo-input')?.click()}
                        className="w-24 h-24 rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center cursor-pointer hover:border-[#6366F1] hover:bg-[#EEF2FF] transition-all overflow-hidden group"
                        data-testid="char-photo-upload-area"
                      >
                        {photoPreview ? (
                          <img src={photoPreview} alt="Preview" className="w-full h-full object-cover rounded-xl" />
                        ) : (
                          <div className="text-center">
                            <Upload className="w-6 h-6 text-[#94A3B8] group-hover:text-[#6366F1] mx-auto mb-1 transition-colors" />
                            <span className="text-[10px] text-[#94A3B8] group-hover:text-[#6366F1]">Upload</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-[#64748B]">Optional: upload a reference photo for more consistent images and videos. You can skip this and add one later.</p>
                        <p className="text-xs text-[#94A3B8] mt-1">JPEG, PNG, WEBP (max 10MB)</p>
                        {photoPreview && (
                          <button type="button" onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                            className="text-xs text-red-500 hover:underline mt-1" data-testid="remove-photo-btn">Remove photo</button>
                        )}
                      </div>
                    </div>
                    <input id="char-photo-input" type="file" accept="image/jpeg,image/png,image/webp"
                      onChange={handlePhotoSelect} className="hidden" data-testid="char-photo-file-input" />
                  </div>

                  {/* Image Style Conversion - Show after character is created and photo uploaded */}
                  {showStyleOptions && uploadedCharacterUrl && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }} 
                      animate={{ opacity: 1, height: 'auto' }}
                      className="space-y-3 p-4 bg-gradient-to-br from-[#EEF2FF] to-[#F0F9FF] rounded-xl border border-[#6366F1]/20"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <TwinneeLogo className="w-4 h-4 text-[#6366F1]" />
                        <h4 className="font-semibold text-sm text-[#1E293B]">AI Style Conversion</h4>
                        <Badge className="text-[10px] bg-[#6366F1]/10 text-[#6366F1]">Powered by Gemini</Badge>
                      </div>

                      {/* Style Selector */}
                      <div className="space-y-2">
                        <Label className="text-xs">Artistic Style</Label>
                        <Select value={selectedStyle} onValueChange={setSelectedStyle} disabled={converting}>
                          <SelectTrigger className="h-10 rounded-lg bg-white" data-testid="style-select">
                            <SelectValue placeholder="Select style" />
                          </SelectTrigger>
                          <SelectContent>
                            {loadingTemplates ? (
                              <SelectItem value="loading" disabled>Loading styles...</SelectItem>
                            ) : templates.length > 0 ? (
                              templates.map(t => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.name}
                                  <span className="text-xs text-[#94A3B8] ml-2">• {t.description}</span>
                                </SelectItem>
                              ))
                            ) : (
                              <>
                                <SelectItem value="cartoon">Cartoon (Disney Style)</SelectItem>
                                <SelectItem value="anime">Anime (Ghibli)</SelectItem>
                                <SelectItem value="pixar">Pixar 3D</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Convert Button */}
                      <Button
                        type="button"
                        onClick={handleConvertStyle}
                        disabled={converting}
                        data-testid="convert-style-btn"
                        className="w-full h-10 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm"
                      >
                        {converting ? (
                          <>
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                              className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full mr-2"
                            />
                            Converting...
                          </>
                        ) : (
                          <>
                            <Wand2 className="w-4 h-4 mr-2" /> Apply {selectedStyle.charAt(0).toUpperCase() + selectedStyle.slice(1)} Style
                          </>
                        )}
                      </Button>

                      {/* Show result when conversion is complete */}
                      {styledResult && (
                        <motion.div 
                          initial={{ opacity: 0, y: 10 }} 
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-3"
                        >
                          <div className="grid grid-cols-2 gap-3">
                            {/* Original */}
                            <div 
                              onClick={() => setUseStyledVersion(false)}
                              className={`cursor-pointer rounded-lg border-2 overflow-hidden transition-all ${
                                !useStyledVersion 
                                  ? 'border-[#6366F1] ring-2 ring-[#6366F1]/20' 
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}
                              data-testid="select-original-btn"
                            >
                              <div className="aspect-square bg-slate-100">
                                <img 
                                  src={photoPreview || `${BACKEND_URL}${uploadedCharacterUrl}`} 
                                  alt="Original" 
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className={`p-2 text-center text-xs font-medium ${
                                !useStyledVersion ? 'bg-[#6366F1] text-white' : 'bg-white text-[#64748B]'
                              }`}>
                                Original
                              </div>
                            </div>

                            {/* Styled */}
                            <div 
                              onClick={() => setUseStyledVersion(true)}
                              className={`cursor-pointer rounded-lg border-2 overflow-hidden transition-all ${
                                useStyledVersion 
                                  ? 'border-[#6366F1] ring-2 ring-[#6366F1]/20' 
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}
                              data-testid="select-styled-btn"
                            >
                              <div className="aspect-square bg-slate-100">
                                <img 
                                  src={styledResult?.url} 
                                  alt={`${selectedStyle} style`} 
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className={`p-2 text-center text-xs font-medium ${
                                useStyledVersion ? 'bg-[#6366F1] text-white' : 'bg-white text-[#64748B]'
                              }`}>
                                {selectedStyle.charAt(0).toUpperCase() + selectedStyle.slice(1)}
                              </div>
                            </div>
                          </div>
                          <p className="text-xs text-[#64748B] text-center">
                            Select which version to use for this character
                          </p>
                        </motion.div>
                      )}
                    </motion.div>
                  )}

                  {/* Name */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Name <span className="text-red-500">*</span></Label>
                    <Input data-testid="char-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Character name" className="h-12 rounded-xl border-2 border-slate-200 focus:border-[#6366F1]" required />
                  </div>

                  {/* Role */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Role <span className="text-xs font-normal text-[#94A3B8]">(optional)</span></Label>
                    <Select
                      value={form.role}
                      onValueChange={(v) => setForm({
                        ...form,
                        role: v,
                        species: v === 'animal'
                          ? (form.species && form.species !== 'human' ? form.species : 'dog')
                          : (v === 'narrator' ? 'human' : (form.species || 'human')),
                      })}
                    >
                      <SelectTrigger className="h-12 rounded-xl border-2 border-slate-200" data-testid="char-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {roles.map(r => <SelectItem key={r} value={r}><span className="capitalize">{r}</span></SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Species — locks body plan across scenes */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Species <span className="text-xs font-normal text-[#94A3B8]">(keeps looks consistent)</span></Label>
                    <Select value={form.species || 'human'} onValueChange={(v) => setForm({ ...form, species: v })}>
                      <SelectTrigger className="h-12 rounded-xl border-2 border-slate-200" data-testid="char-species-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['human', 'dog', 'cat', 'bird', 'rabbit', 'horse', 'bear', 'fox', 'animal'].map((s) => (
                          <SelectItem key={s} value={s}><span className="capitalize">{s}</span></SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Gender */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Gender <span className="text-xs font-normal text-[#94A3B8]">(optional)</span></Label>
                    <Select value={form.gender || 'not_specified'} onValueChange={(v) => setForm({ ...form, gender: v })}>
                      <SelectTrigger className="h-12 rounded-xl border-2 border-slate-200" data-testid="char-gender-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {genderOptions.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Description <span className="text-xs font-normal text-[#94A3B8]">(optional)</span></Label>
                    <Textarea data-testid="char-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Describe your character..." className="rounded-xl border-2 border-slate-200 focus:border-[#6366F1] min-h-[80px]" />
                  </div>

                  {/* Personality sliders */}
                  <div className="space-y-4 p-4 rounded-xl border border-slate-200 bg-slate-50/80">
                    <Label className="text-xs font-semibold tracking-wide text-[#64748B] uppercase">Personality sliders</Label>
                    <p className="text-xs text-[#94A3B8] leading-relaxed">
                      Tune how this character tends to behave in stories (used when generating scenes and dialogue).
                    </p>
                    <div className="space-y-4">
                      {personalitySliderFields.map(({ key, label }) => (
                        <div key={key} className="space-y-1.5" data-testid={`personality-slider-${key}`}>
                          <div className="flex justify-between items-center gap-3">
                            <span className="text-sm font-medium text-[#334155]">{label}</span>
                            <span className="text-sm font-semibold text-[#6366F1] tabular-nums">
                              {(form.personality_profile?.[key] ?? defaultPersonalityProfile[key])}%
                            </span>
                          </div>
                          <input
                            type="range"
                            min={0}
                            max={100}
                            value={form.personality_profile?.[key] ?? defaultPersonalityProfile[key]}
                            onChange={(e) => {
                              const value = Number(e.target.value);
                              setForm((prev) => ({
                                ...prev,
                                personality_profile: {
                                  ...defaultPersonalityProfile,
                                  ...prev.personality_profile,
                                  [key]: Number.isFinite(value) ? value : 0,
                                },
                              }));
                            }}
                            className="w-full h-1.5 rounded-full accent-[#6366F1] bg-slate-200 appearance-none cursor-pointer"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Voice style (narration preset) */}
                  <div className="space-y-2 p-4 rounded-xl border border-slate-200 bg-slate-50/80">
                    <Label className="text-xs font-semibold tracking-wide text-[#64748B] uppercase">Voice style</Label>
                    <p className="text-xs text-[#94A3B8] leading-relaxed mb-2">
                      Preset for narrator-style Edge voices. Dialogue can still use provider voices below.
                    </p>
                    <div className="flex gap-2 items-center">
                      <Select
                        value={form.voice_style === '' ? '__none__' : form.voice_style}
                        onValueChange={(v) => setForm({ ...form, voice_style: v === '__none__' ? '' : v })}
                      >
                        <SelectTrigger className="h-12 flex-1 rounded-xl border-2 border-slate-200 bg-white" data-testid="char-voice-style-select">
                          <SelectValue placeholder="Voice style" />
                        </SelectTrigger>
                        <SelectContent>
                          {voiceStyleOptions.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-12 w-12 shrink-0 rounded-full border-2 border-slate-200"
                        disabled={!form.voice_style || previewingVoiceStyle}
                        onClick={playVoiceStylePreview}
                        data-testid="char-voice-style-preview-btn"
                        aria-label="Preview voice style"
                      >
                        {previewingVoiceStyle ? (
                          <Loader2 className="w-5 h-5 animate-spin text-[#6366F1]" />
                        ) : (
                          <Play className="w-5 h-5 text-[#6366F1] fill-[#6366F1]/20" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Traits */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Personality traits <span className="text-xs font-normal text-[#94A3B8]">(optional)</span></Label>
                    <div className="flex flex-wrap gap-2">
                      {traitOptions.map(trait => (
                        <button key={trait} type="button" onClick={() => toggleTrait(trait)} data-testid={`trait-${trait}`}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            form.personality_traits.includes(trait)
                              ? 'bg-[#6366F1] text-white shadow-md shadow-[#6366F1]/25'
                              : 'bg-slate-100 text-[#64748B] hover:bg-slate-200'
                          }`}>
                          {trait}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Speaking Style */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Speaking style <span className="text-xs font-normal text-[#94A3B8]">(optional)</span></Label>
                    <Select value={form.speaking_style} onValueChange={(v) => setForm({ ...form, speaking_style: v })}>
                      <SelectTrigger className="h-12 rounded-xl border-2 border-slate-200" data-testid="char-speaking-style-select"><SelectValue placeholder="Select style" /></SelectTrigger>
                      <SelectContent>
                        {speakingStyles.map(s => <SelectItem key={s} value={s}><span className="capitalize">{s}</span></SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Label className="text-sm font-medium">Dialogue voices (story audio)</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        disabled={ttsCatalogLoading}
                        onClick={() => loadTtsVoiceCatalogs()}
                      >
                        {ttsCatalogLoading ? 'Loading voices…' : 'Refresh voice lists'}
                      </Button>
                    </div>
                    <p className="text-xs text-[#64748B]">
                      Choose a Dialogue TTS Preference, then pick the matching voice ID for this character.
                    </p>
                    <div className="space-y-2">
                      <Label className="text-xs text-[#64748B]">Dialogue TTS preference</Label>
                      <Select
                        value={form.dialogue_tts_provider || 'runway'}
                        onValueChange={(v) => setForm({ ...form, dialogue_tts_provider: v })}
                      >
                        <SelectTrigger className="h-10 rounded-lg bg-white border-2 border-slate-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto (Murf if configured, else ElevenLabs)</SelectItem>
                          <SelectItem value="minimax">Prefer MiniMax</SelectItem>
                          <SelectItem value="murf">Prefer Murf</SelectItem>
                          <SelectItem value="elevenlabs">Prefer ElevenLabs</SelectItem>
                          <SelectItem value="runway">Prefer Runway ML audio</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {form.dialogue_tts_provider && form.dialogue_tts_provider !== 'auto' ? (
                      <div className="space-y-2 rounded-lg border border-slate-200 bg-white/80 p-3">
                        <Label className="text-xs font-semibold text-[#1E293B]">
                          {voiceProviderLabel[form.dialogue_tts_provider]} voice ID
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            value={
                              form.dialogue_tts_provider === 'elevenlabs'
                                ? form.elevenlabs_voice_id || ''
                                : form.dialogue_tts_provider === 'murf'
                                  ? form.murf_voice_id || ''
                                  : form.dialogue_tts_provider === 'runway'
                                    ? form.runway_voice_id || ''
                                  : form.minimax_voice_id || ''
                            }
                            readOnly
                            placeholder={`Choose a ${voiceProviderLabel[form.dialogue_tts_provider]} voice`}
                            className="h-10 rounded-lg bg-white border-2 border-slate-200 font-mono text-sm"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => openVoicePicker(form.dialogue_tts_provider)}
                            className="h-10 rounded-lg"
                            disabled={ttsCatalogLoading}
                          >
                            Choose
                          </Button>
                        </div>
                        {form.dialogue_tts_provider === 'runway' && (
                          <div className="space-y-1.5">
                            <Label className="text-xs text-[#64748B]">Runway audio model</Label>
                            <Select
                              value={form.runway_model || 'eleven_multilingual_v2'}
                              onValueChange={(v) => setForm({ ...form, runway_model: v })}
                            >
                              <SelectTrigger className="h-10 rounded-lg bg-white border-2 border-slate-200">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {runwayAudioModels.map((m) => (
                                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-slate-200 bg-white/70 p-3 text-xs text-[#64748B]">
                        Select MiniMax, Murf, ElevenLabs, or Runway ML to choose a specific character voice ID.
                      </div>
                    )}
                  </div>

                  {editId && (
                    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <LayoutGrid className="h-4 w-4 text-[#6366F1]" />
                          <Label className="text-sm font-semibold">Runway turnaround (8 shots)</Label>
                        </div>
                        <Badge variant="secondary" className="text-[10px]">
                          Optional • uses RUNWAY_API_KEY on server
                        </Badge>
                      </div>
                      <p className="text-xs text-[#64748B] leading-relaxed">
                        Builds a structured sheet from this character + creative_profile, runs eight separate{' '}
                        <span className="font-mono">gen4_image</span> generations (same seed), and saves paths under{' '}
                        <span className="font-mono text-[11px]">creative_profile.character.referenceFrames</span>. If you
                        upload a portrait, it is sent as Runway reference for consistency.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="rounded-lg gap-1.5"
                          onClick={openReferenceSheetPreview}
                          data-testid="char-builder-ref-sheet-preview"
                        >
                          <LayoutGrid className="h-4 w-4" />
                          Preview sheet JSON
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="rounded-lg gap-1.5"
                          disabled={!!refSheetGeneratingByChar[editId]}
                          onClick={runReferenceSheetGeneration}
                          data-testid="char-builder-ref-sheet-generate"
                        >
                          {!!refSheetGeneratingByChar[editId] ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <TwinneeLogo className="h-4 w-4" />
                          )}
                          Generate 8 Runway angles
                        </Button>
                      </div>
                      <div className="rounded-lg border border-violet-100 bg-violet-50/50 p-3">
                        <p className="mb-2 text-xs font-semibold text-[#5B21B6]">Runway Live avatar</p>
                        <p className="mb-2 text-[11px] leading-relaxed text-[#64748B]">
                          After <span className="font-semibold">faceFront</span> exists in the grid, create a Runway Live
                          avatar (<span className="font-mono text-[10px]">POST /v1/avatars</span>) using that frame as the
                          reference image.
                        </p>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                          <div className="min-w-[11rem] flex-1">
                            <Label className="mb-1 block text-[10px] font-medium text-[#64748B]">Preset voice</Label>
                            <Select
                              value={runwayLiveAvatarVoice}
                              onValueChange={setRunwayLiveAvatarVoice}
                              disabled={runwayLiveAvatarBusy}
                            >
                              <SelectTrigger className="h-9 rounded-lg border-2 border-slate-200 bg-white text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="max-h-[min(50vh,18rem)]">
                                {RUNWAY_LIVE_AVATAR_PRESETS.map((p) => (
                                  <SelectItem key={p.value} value={p.value}>
                                    {p.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            className="rounded-lg bg-violet-600 text-white hover:bg-violet-700"
                            disabled={
                              !runwayFaceFrontReady ||
                              runwayLiveAvatarBusy ||
                              !!refSheetGeneratingByChar[editId]
                            }
                            onClick={createRunwayLiveAvatarFromBuilder}
                            data-testid="char-builder-runway-live-avatar"
                          >
                            {runwayLiveAvatarBusy ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <TwinneeLogo className="h-4 w-4" />
                            )}
                            {runwayLiveAvatarBusy ? 'Creating…' : 'Create Runway Live avatar'}
                          </Button>
                        </div>
                        {currentEditedCharacter?.runway_live_avatar?.id ? (
                          <p className="mt-2 font-mono text-[9px] text-[#94A3B8]">
                            Last id: {String(currentEditedCharacter.runway_live_avatar.id)}
                            {currentEditedCharacter.runway_live_avatar.status != null
                              ? ` · ${String(currentEditedCharacter.runway_live_avatar.status)}`
                              : ''}
                          </p>
                        ) : null}
                      </div>
                      <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
                        {CHARACTER_REFERENCE_GRID.map(({ key, label }) => {
                          const rf = editId ? refSheetSnapshots[editId] : null;
                          const src = gridSrcForAssetPath(rf?.[key]);
                          return (
                            <div key={key} className="overflow-hidden rounded-lg border bg-white shadow-sm">
                              <div className="aspect-square bg-slate-100">
                                {src ? (
                                  <img src={src} alt="" className="h-full w-full object-cover" />
                                ) : (
                                  <div className="flex h-full items-center justify-center p-1 text-[9px] text-[#94A3B8]">
                                    —
                                  </div>
                                )}
                              </div>
                              <div className="truncate px-0.5 py-0.5 text-center text-[9px] text-[#64748B]">{label}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Imaginary */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div>
                      <Label className="text-sm font-medium">Imaginary Character</Label>
                      <p className="text-xs text-[#94A3B8]">Not based on a real person</p>
                    </div>
                    <Switch data-testid="char-imaginary-switch" checked={form.is_imaginary} onCheckedChange={(v) => setForm({ ...form, is_imaginary: v })} />
                  </div>

                  <Button type="submit" disabled={saving} data-testid="save-character-btn"
                    className="w-full h-12 rounded-full bg-[#6366F1] hover:bg-[#4F46E5] text-white font-accent shadow-lg shadow-[#6366F1]/25 hover:scale-[1.02] active:scale-95 transition-all">
                    {saving ? 'Saving...' : editId ? 'Update Character' : 'Create Character'}
                  </Button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Character Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-3xl p-6 border border-slate-100 animate-pulse">
                <div className="h-12 w-12 bg-slate-200 rounded-2xl mb-4" />
                <div className="h-5 bg-slate-200 rounded-lg w-2/3 mb-3" />
                <div className="h-4 bg-slate-100 rounded w-full mb-2" />
              </div>
            ))}
          </div>
        ) : characters.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-panel rounded-3xl p-16 text-center">
            <Users className="w-16 h-16 text-[#F59E0B]/30 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-[#1E293B] mb-2">No characters yet</h3>
            <p className="text-[#64748B] mb-6">Create your first character to start building stories</p>
            <Button onClick={() => setShowForm(true)} data-testid="empty-add-character-btn"
              className="rounded-full bg-[#6366F1] hover:bg-[#4F46E5] text-white font-accent px-8 shadow-lg shadow-[#6366F1]/25">
              <Wand2 className="w-5 h-5 mr-2" /> Create Character
            </Button>
          </motion.div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-h-[min(70vh,48rem)] overflow-y-auto pr-1 -mr-1">
            {characters.map((char, i) => (
              <motion.div key={char.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }} whileHover={{ y: -5 }}
                className="bg-white rounded-3xl p-6 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.06)] border border-slate-100 hover:shadow-[0_20px_60px_-15px_rgba(99,102,241,0.12)] transition-shadow group"
                data-testid={`character-card-${char.id}`}>

                <div className="flex items-start justify-between mb-4">
                  {/* Avatar / Photo */}
                  <div className="relative">
                    {char.reference_image ? (
                      <div className="w-14 h-14 rounded-2xl overflow-hidden border-2 border-slate-100 shadow-sm">
                        <img src={char.reference_image.startsWith('/api') ? `${BACKEND_URL}${char.reference_image}` : char.reference_image} alt={char.name}
                          className="w-full h-full object-cover" data-testid={`char-photo-${char.id}`} />
                      </div>
                    ) : (
                      <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-heading font-bold text-lg"
                        style={{ backgroundColor: roleColors[char.role] || '#6366F1' }}>
                        {char.name[0]?.toUpperCase()}
                      </div>
                    )}
                    {/* Upload photo overlay */}
                    <button
                      onClick={() => handleUploadForExisting(char.id)}
                      disabled={uploadingPhoto === char.id}
                      data-testid={`upload-photo-${char.id}`}
                      className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#6366F1] text-white flex items-center justify-center shadow-md hover:scale-110 transition-transform"
                    >
                      {uploadingPhoto === char.id ? (
                        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="w-3 h-3 border border-white/30 border-t-white rounded-full" />
                      ) : (
                        <Camera className="w-3 h-3" />
                      )}
                    </button>
                  </div>

                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => startEdit(char)} data-testid={`edit-char-${char.id}`}
                      className="p-2 text-[#94A3B8] hover:text-[#6366F1] rounded-lg hover:bg-[#EEF2FF]">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(char.id)} data-testid={`delete-char-${char.id}`}
                      className="p-2 text-[#94A3B8] hover:text-red-500 rounded-lg hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <h3 className="font-heading font-bold text-lg text-[#1E293B] mb-1">{char.name}</h3>
                <Badge className="rounded-full text-xs font-medium mb-3 capitalize"
                  style={{ backgroundColor: (roleColors[char.role] || '#6366F1') + '15', color: roleColors[char.role] || '#6366F1' }}>
                  {char.role}
                </Badge>
                {char.gender && char.gender !== 'not_specified' && (
                  <Badge className="rounded-full text-xs font-medium mb-3 ml-2 capitalize bg-slate-100 text-[#64748B]">
                    {char.gender.replace('_', ' ')}
                  </Badge>
                )}
                {char.reference_image && (
                  <div className="flex items-center gap-1 text-xs text-[#10B981] mb-2">
                    <ImageIcon className="w-3 h-3" /> Photo reference attached
                  </div>
                )}
                {char.description && <p className="text-sm text-[#64748B] mb-3 line-clamp-2">{char.description}</p>}
                {char.personality_traits?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {char.personality_traits.slice(0, 4).map(t => (
                      <span key={t} className="text-xs px-2 py-1 rounded-full bg-slate-100 text-[#64748B]">{t}</span>
                    ))}
                    {char.personality_traits.length > 4 && (
                      <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-[#64748B]">+{char.personality_traits.length - 4}</span>
                    )}
                  </div>
                )}
                {char.is_imaginary && (
                  <div className="mt-3 flex items-center gap-1 text-xs text-[#EC4899]">
                    <TwinneeLogo className="w-3 h-3" /> Imaginary
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      <Dialog open={refSheetDialogOpen} onOpenChange={setRefSheetDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-[min(94vw,40rem)] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle>Character reference sheet (preview)</DialogTitle>
            <DialogDescription className="text-xs">
              Structured prompts for Runway — no images generated until you use “Generate 8 Runway angles”.
            </DialogDescription>
          </DialogHeader>
          {creativeProfilePortraitSrc ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-[#475569]">Uploaded portrait preview</p>
              <div className="w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <img
                  src={creativeProfilePortraitSrc}
                  alt="Uploaded portrait"
                  className="h-52 w-full object-contain bg-white"
                />
              </div>
            </div>
          ) : null}
          <pre className="max-h-[55vh] overflow-auto rounded-xl border bg-slate-950 p-3 text-[10px] text-slate-100">
            {refSheetDialogText}
          </pre>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-xl" onClick={() => setRefSheetDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NarrationVoicePickerDialog
        open={voicePickerOpen}
        onOpenChange={setVoicePickerOpen}
        provider={voicePickerProvider}
        language={(form.murf_locale || 'en').split('-')[0] || 'en'}
        onSelectVoice={selectCharacterVoice}
      />
    </div>
  );
};
